export function degreeAt(time: number, duration: number) {
  return duration > 0
    ? Math.round(Math.max(0, Math.min(1, time / duration)) * 360)
    : 0
}

/** Coalesce pointer input while the decoder is busy; report only presented frames. */
export function attachScrubber(
  video: HTMLVideoElement,
  onPaint: (degree: number) => void,
) {
  let pending: number | null = null
  let frame: number | null = null
  let disposed = false
  let activated = false
  let lastDegree = 0
  // The final 24 fps sample is the returning front view, before MP4 duration.
  const lastFrameTime = () => Math.max(0, video.duration - 1 / 24)
  function observePaint() {
    if (frame !== null || !video.requestVideoFrameCallback) return
    frame = video.requestVideoFrameCallback((_now, metadata) => {
      frame = null
      if (disposed) return
      onPaint(degreeAt(metadata.mediaTime, lastFrameTime()))
      if (
        video.seeking ||
        pending !== null ||
        Math.abs(metadata.mediaTime - video.currentTime) > 1 / 24
      )
        observePaint()
    })
  }
  function seek(degree: number) {
    lastDegree = Math.max(0, Math.min(360, degree))
    if (!Number.isFinite(video.duration) || video.duration <= 0) return
    pending = Math.max(
      0,
      Math.min(lastFrameTime(), (degree / 360) * lastFrameTime()),
    )
    observePaint()
    if (!video.seeking) {
      video.currentTime = pending
      pending = null
    }
  }
  function seeked() {
    observePaint()
    if (!video.requestVideoFrameCallback)
      onPaint(degreeAt(video.currentTime, lastFrameTime()))
    if (pending !== null) {
      const target = pending
      pending = null
      if (Math.abs(video.currentTime - target) > 1 / 48) {
        observePaint()
        video.currentTime = target
      }
    }
  }
  video.addEventListener('seeked', seeked)
  return {
    seek,
    nudge(delta: number) {
      seek(lastDegree + delta)
    },
    activate() {
      if (activated) return
      activated = true
      void video
        .play()
        .then(() => {
          video.pause()
          if (!disposed) seek(lastDegree)
        })
        .catch(() => {
          activated = false
        })
    },
    dispose() {
      disposed = true
      video.pause()
      video.removeEventListener('seeked', seeked)
      if (frame !== null) video.cancelVideoFrameCallback(frame)
    },
  }
}
